import React, { useState, useEffect, useRef, useMemo, useCallback, startTransition } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { MultiSelectDropdown } from "../ui/multi-select-dropdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowLeft, Save, Loader2, ExternalLink, Plus, FileText, Link, Upload, X, Bold, Italic, Underline, List, ListOrdered, MessageSquare } from "lucide-react";
import { LinkDocumentDialog } from "../planning/LinkDocumentDialog";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

interface InformationEntry {
  information_name: string;
  workspace: string[];
  workspace_prefix: string[];
  file_name: string;
  testing_reference?: string;
  information_type?: string;
  responses?: {
    internal_source_data?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
    internal_report_logic?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
    internal_report_parameters?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
    test_accuracy_approach?: string;
    test_accuracy?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
    test_completeness_approach?: string;
    test_completeness?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
    usage?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
    sufficient_precision?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
    sufficient_reliability?: {
      textResponse?: { html: string; plainText: string };
      documents?: Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>;
    };
  };
}

interface Workspace {
  name: string;
  prefix: string;
  area: "Planning" | "Execution" | "ConcludingProcedures";
}

interface InformationDetailViewProps {
  informationName: string;
  onBack: () => void;
}

export function InformationDetailView({ informationName, onBack }: InformationDetailViewProps) {
  const [information, setInformation] = useState<InformationEntry | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [testingReference, setTestingReference] = useState("");
  const [informationType, setInformationType] = useState<string>("");
  const [sharePointFiles, setSharePointFiles] = useState<Array<{ name: string }>>([]);
  const [isLoadingSharePointFiles, setIsLoadingSharePointFiles] = useState(false);

  // Question states
  const [questionStates, setQuestionStates] = useState<{ [key: string]: any }>({});
  // Review comment: which question's comments are open
  const [activeReviewComment, setActiveReviewComment] = useState<{
    questionId: string;
    questionText: string;
    workspace?: string;
    subPhase?: string;
  } | null>(null);

  useEffect(() => {
    loadWorkspaces();
    loadSharePointFiles();
  }, []);

  useEffect(() => {
    if (informationName) {
      loadInformation();
    }
  }, [informationName]);

  const loadWorkspaces = async () => {
    try {
      if ((window as any).sharePointAPI?.downloadSectionsJson) {
        const result = await (window as any).sharePointAPI.downloadSectionsJson();
        if (result?.success && result?.data) {
          const allWorkspaces: Workspace[] = [];
          const areas: Array<"Planning" | "Execution" | "ConcludingProcedures"> = [
            "Planning",
            "Execution",
            "ConcludingProcedures",
          ];
          
          areas.forEach((area) => {
            if (result.data[area] && typeof result.data[area] === "object") {
              Object.entries(result.data[area]).forEach(([name, prefix]) => {
                allWorkspaces.push({
                  name: String(name),
                  prefix: String(prefix),
                  area,
                });
              });
            }
          });
          
          allWorkspaces.sort((a, b) => a.name.localeCompare(b.name));
          setWorkspaces(allWorkspaces);
        }
      }
    } catch (err) {
      console.error("Error loading workspaces:", err);
    }
  };

  const loadSharePointFiles = async () => {
    setIsLoadingSharePointFiles(true);
    try {
      if ((window as any).sharePointAPI?.loadCloudFiles) {
        const result = await (window as any).sharePointAPI.loadCloudFiles();
        if (result?.success && result?.data) {
          setSharePointFiles(result.data.map((file: any) => ({ name: file.name })));
        }
      }
    } catch (error) {
      console.error("Error loading SharePoint files:", error);
    } finally {
      setIsLoadingSharePointFiles(false);
    }
  };

  const loadInformation = async () => {
    setLoading(true);
    try {
      if ((window as any).sharePointAPI?.downloadLibrariesInformation) {
        const result = await (window as any).sharePointAPI.downloadLibrariesInformation();
        if (result?.success && result?.data?.information) {
          const entry = result.data.information.find(
            (item: InformationEntry) => item.information_name === informationName
          );
          if (entry) {
            setInformation(entry);
            setSelectedWorkspaces(entry.workspace || []);
            setTestingReference(entry.testing_reference || "");
            setInformationType(entry.information_type || "");
            
            // Initialize question states
            const states: { [key: string]: any } = {};
            if (entry.responses) {
              Object.keys(entry.responses).forEach((key) => {
                states[key] = entry.responses![key as keyof typeof entry.responses];
              });
            }
            setQuestionStates(states);
          }
        }
      }
    } catch (error) {
      console.error("Error loading information:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionCodeForQuestion = (questionId: string, workspaceName: string): string => {
    const workspace = workspaces.find((w) => w.name === workspaceName);
    if (!workspace) return "";
    
    const prefix = workspace.prefix;
    const phaseName = workspace.area === "ConcludingProcedures" ? "ConcludingProcedures" : workspace.area;
    const normalizedInfoName = informationName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .trim();
    
    return `${phaseName}_${prefix}_${normalizedInfoName}-${questionId}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if ((window as any).sharePointAPI?.updateInformationEntry) {
        // Get workspace prefixes for selected workspaces
        const workspacePrefixes = selectedWorkspaces.map((wsName) => {
          const ws = workspaces.find((w) => w.name === wsName);
          return ws?.prefix || "";
        }).filter(Boolean);

        const result = await (window as any).sharePointAPI.updateInformationEntry({
          informationName,
          workspace: selectedWorkspaces,
          workspace_prefix: workspacePrefixes,
          testingReference,
          informationType,
          responses: {
            ...questionStates,
          },
        });
        
        if (result?.success) {
          alert("Information saved successfully!");
          await loadInformation();
        } else {
          alert(`Failed to save: ${result?.error || "Unknown error"}`);
        }
      } else {
        alert("API method not available");
      }
    } catch (error) {
      console.error("Error saving information:", error);
      alert("Error saving information");
    } finally {
      setSaving(false);
    }
  };

  const handleFileOperation = async (
    questionId: string,
    fileName: string,
    operation: "add" | "remove"
  ) => {
    if (!information) return;
    
    const sectionCodes: string[] = [];
    for (const workspaceName of selectedWorkspaces) {
      const sectionCode = getSectionCodeForQuestion(questionId, workspaceName);
      if (sectionCode) {
        sectionCodes.push(sectionCode);
      }
    }

    if (sectionCodes.length > 0) {
      try {
        if (operation === "add") {
          // Update db.json with all section codes
          for (const sectionCode of sectionCodes) {
            if ((window as any).sharePointAPI?.updateFileSectionInDb) {
              await (window as any).sharePointAPI.updateFileSectionInDb(
                fileName,
                sectionCode,
                "client"
              );
            }
          }
        } else {
          // Remove section codes
          for (const sectionCode of sectionCodes) {
            if ((window as any).sharePointAPI?.removeFileSection) {
              await (window as any).sharePointAPI.removeFileSection({
                fileName,
                sectionToRemove: sectionCode,
              });
            }
          }
        }
      } catch (error) {
        console.error(`Error ${operation === "add" ? "adding" : "removing"} file sections:`, error);
      }
    }
  };

  const handleOpenFile = async (fileName: string) => {
    try {
      let fileUrl: string | undefined;
      
      // Try to get file URL from loadCloudFiles API first
      if ((window as any).sharePointAPI?.loadCloudFiles) {
        const result = await (window as any).sharePointAPI.loadCloudFiles();
        if (result?.success && Array.isArray(result?.data?.files)) {
          const file = (result.data.files as { name: string; url?: string }[]).find(
            (f: any) => f.name === fileName
          );
          fileUrl = file?.url;
        }
      }
      
      // If URL not found, construct SharePoint URL
      if (!fileUrl) {
        const fyYear = getFY_YEAR();
        const config = (window as any).sharePointConfig || {};
        const siteHostname = config.siteHostname || "";
        const sitePath = config.sitePath || "";
        const docLibrary = config.docLibrary || "";
        
        fileUrl = `https://${siteHostname}${sitePath}/_layouts/15/download.aspx?SourceUrl=${encodeURIComponent(`/${sitePath}/${docLibrary}/${fyYear}/juggernaut/${fileName}`)}`;
      }
      
      // Use Electron's shell.openExternal to open in default browser (Chrome)
      if ((window as any).electronWindow?.openExternal) {
        try {
          await (window as any).electronWindow.openExternal(fileUrl);
        } catch (error) {
          console.error("Error opening external URL:", error);
          // Fallback to window.open if Electron API fails
          window.open(fileUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        // Fallback to window.open if Electron API is not available
        window.open(fileUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error opening file:", error);
      alert("Error opening file");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!information) {
    return (
      <div className="p-6">
        <Button variant="outline" onClick={onBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <p className="text-white">Information not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save
            </>
          )}
        </Button>
      </div>

      <Card className="border-white/10 bg-white/5">
        <CardHeader>
          <CardTitle className="text-white">Information Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-white">Information Name</TableHead>
                <TableHead className="text-white">Workspace</TableHead>
                <TableHead className="text-white">Information Type</TableHead>
                <TableHead className="text-white">Linked File</TableHead>
                <TableHead className="text-white">Testing Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="text-white">{information.information_name}</TableCell>
                <TableCell>
                  <MultiSelectDropdown
                    options={workspaces.map((w) => w.name)}
                    value={selectedWorkspaces}
                    onChange={setSelectedWorkspaces}
                    placeholder="Select workspace(s)"
                    className="w-full"
                  />
                </TableCell>
                <TableCell>
                  <Select value={informationType} onValueChange={setInformationType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="System generated">System generated</SelectItem>
                      <SelectItem value="Non-system generated">Non-system generated</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {information.file_name ? (
                    <Button
                      variant="link"
                      className="text-blue-400 hover:text-blue-300 p-0 h-auto"
                      onClick={() => handleOpenFile(information.file_name)}
                      title="Open file"
                      data-allow-archival-edit={true}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      {information.file_name}
                    </Button>
                  ) : (
                    <span className="text-white/60">No file linked</span>
                  )}
                </TableCell>
                <TableCell>
                  <Input
                    value={testingReference}
                    onChange={(e) => setTestingReference(e.target.value)}
                    placeholder="Enter testing reference"
                    className="bg-black/40 border-white/20 text-white"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Questions */}
      <div className="space-y-6">
        {/* Question 1 */}
        <QuestionWithDocuments
          title="1. Document the elements of internal information source data"
          questionId="internal_source_data"
          responseData={questionStates.internal_source_data}
          onUpdate={(data) => setQuestionStates({ ...questionStates, internal_source_data: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-internal_source_data`,
              questionText: `1. Document the elements of internal information source data - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />

        {/* Question 2 */}
        <QuestionWithDocuments
          title="2. Document the elements of internal information report logic"
          questionId="internal_report_logic"
          responseData={questionStates.internal_report_logic}
          onUpdate={(data) => setQuestionStates({ ...questionStates, internal_report_logic: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-internal_report_logic`,
              questionText: `2. Document the elements of internal information report logic - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />

        {/* Question 3 */}
        <QuestionWithDocuments
          title="3. Document the elements of internal information report parameters"
          questionId="internal_report_parameters"
          responseData={questionStates.internal_report_parameters}
          onUpdate={(data) => setQuestionStates({ ...questionStates, internal_report_parameters: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-internal_report_parameters`,
              questionText: `3. Document the elements of internal information report parameters - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />

        {/* Questions 4-5 with dropdown */}
        <QuestionWithDropdownAndDocuments
          title="4. Approach to test the accuracy of internal information"
          dropdownQuestionId="test_accuracy_approach"
          documentsQuestionId="test_accuracy"
          dropdownValue={questionStates.test_accuracy_approach}
          responseData={questionStates.test_accuracy}
          onDropdownChange={(value) => setQuestionStates({ ...questionStates, test_accuracy_approach: value })}
          onDocumentsUpdate={(data) => setQuestionStates({ ...questionStates, test_accuracy: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-test_accuracy`,
              questionText: `4. Approach to test the accuracy of internal information - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />

        <QuestionWithDropdownAndDocuments
          title="5. Approach to test the completeness of internal information"
          dropdownQuestionId="test_completeness_approach"
          documentsQuestionId="test_completeness"
          dropdownValue={questionStates.test_completeness_approach}
          responseData={questionStates.test_completeness}
          onDropdownChange={(value) => setQuestionStates({ ...questionStates, test_completeness_approach: value })}
          onDocumentsUpdate={(data) => setQuestionStates({ ...questionStates, test_completeness: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-test_completeness`,
              questionText: `5. Approach to test the completeness of internal information - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />

        {/* Questions 6-8 */}
        <QuestionWithDocuments
          title="6. Usage of internal information"
          questionId="usage"
          responseData={questionStates.usage}
          onUpdate={(data) => setQuestionStates({ ...questionStates, usage: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-usage`,
              questionText: `6. Usage of internal information - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />

        <QuestionWithDocuments
          title="7. Is internal information sufficiently precise and detailed for audit purposes?"
          questionId="sufficient_precision"
          responseData={questionStates.sufficient_precision}
          onUpdate={(data) => setQuestionStates({ ...questionStates, sufficient_precision: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-sufficient_precision`,
              questionText: `7. Sufficient precision - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />

        <QuestionWithDocuments
          title="8. Is internal information sufficiently reliable for audit purposes?"
          questionId="sufficient_reliability"
          responseData={questionStates.sufficient_reliability}
          onUpdate={(data) => setQuestionStates({ ...questionStates, sufficient_reliability: data })}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={handleFileOperation}
          onReviewCommentClick={() =>
            setActiveReviewComment({
              questionId: `information-${informationName}-sufficient_reliability`,
              questionText: `8. Sufficient reliability - ${informationName}`,
              workspace: "Information",
              subPhase: informationName,
            })
          }
        />
      </div>

      {/* Review Comment Modal */}
      {activeReviewComment && (
        <ReviewComment
          questionId={activeReviewComment.questionId}
          questionText={activeReviewComment.questionText}
          isOpen={true}
          onClose={() => setActiveReviewComment(null)}
          workspace={activeReviewComment.workspace}
          subPhase={activeReviewComment.subPhase}
        />
      )}
    </div>
  );
}

// Helper component for simple question cards
function QuestionCard({ title, questionId, children }: { title: string; questionId: string; children: React.ReactNode }) {
  return (
    <Card className="border-white/10 bg-white/5">
      <CardHeader>
        <CardTitle className="text-white text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// Helper component for questions with documents
function QuestionWithDocuments({
  title,
  questionId,
  responseData,
  onUpdate,
  selectedWorkspaces,
  workspaces,
  informationName,
  sharePointFiles,
  isLoadingSharePointFiles,
  onFileOperation,
  onReviewCommentClick,
}: {
  title: string;
  questionId: string;
  responseData?: any;
  onUpdate: (data: any) => void;
  selectedWorkspaces: string[];
  workspaces: Workspace[];
  informationName: string;
  sharePointFiles: Array<{ name: string }>;
  isLoadingSharePointFiles: boolean;
  onFileOperation: (questionId: string, fileName: string, operation: "add" | "remove") => void;
  onReviewCommentClick?: () => void;
}) {
  const { isArchivalMode } = useArchivalMode();
  const effectiveDisabled = isArchivalMode;
  const [showTextbox, setShowTextbox] = useState(false);
  const [textboxContent, setTextboxContent] = useState("");
  const [documents, setDocuments] = useState<Array<{ id: string; type: string; name: string; reference?: string; url?: string; content?: string }>>([]);
  const [linkedFiles, setLinkedFiles] = useState<Array<{ id: string; type: string; name: string; reference?: string; url?: string }>>([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [hasTextbox, setHasTextbox] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false); // Control dropdown state to prevent conflicts with dialogs
  const loadingRef = useRef(false);
  const textboxEditorRef = useRef<HTMLDivElement>(null);
  const textboxInitializedRef = useRef(false);

  const getSectionCodeForQuestion = (workspaceName: string): string => {
    const workspace = workspaces.find((w) => w.name === workspaceName);
    if (!workspace) return "";
    
    const prefix = workspace.prefix;
    const phaseName = workspace.area === "ConcludingProcedures" ? "ConcludingProcedures" : workspace.area;
    const normalizedInfoName = informationName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .trim();
    
    return `${phaseName}_${prefix}_${normalizedInfoName}-${questionId}`;
  };

  // Load linked files from db.json based on section codes
  const loadLinkedFiles = useCallback(async () => {
    if (loadingRef.current || selectedWorkspaces.length === 0) return;
    loadingRef.current = true;

    try {
      const sectionCodes = selectedWorkspaces
        .map((ws) => getSectionCodeForQuestion(ws))
        .filter(Boolean);

      if (sectionCodes.length === 0) {
        setLinkedFiles([]);
        return;
      }

      // Load files for all section codes
      const allFiles: Array<{ id: string; type: string; name: string; url?: string }> = [];
      const fileNamesSeen = new Set<string>();

      for (const sectionCode of sectionCodes) {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const result = await (window as any).sharePointAPI.loadDocumentListBySection(sectionCode);
          if (result?.success && Array.isArray(result?.data)) {
            for (const file of result.data) {
              if (file.name && !fileNamesSeen.has(file.name)) {
                fileNamesSeen.add(file.name);
                allFiles.push({
                  id: `linked_${file.name}_${Date.now()}`,
                  type: "linked",
                  name: file.name,
                  url: file.url || "",
                });
              }
            }
          }
        }
      }

      setLinkedFiles(allFiles);
    } catch (error) {
      console.error("Error loading linked files:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [selectedWorkspaces, workspaces, informationName, questionId]);

  // Initialize from responseData
  useEffect(() => {
    if (responseData) {
      if (responseData.textResponse?.html) {
        setTextboxContent(responseData.textResponse.html);
        setHasTextbox(true);
        // Show textbox if there's content
        setShowTextbox(true);
      }
      if (responseData.documents) {
        setDocuments(responseData.documents.filter((d: any) => d.type !== "textbox"));
      }
    }
  }, [responseData]);

  // Load linked files when workspaces change
  useEffect(() => {
    if (selectedWorkspaces.length > 0) {
      loadLinkedFiles();
    }
  }, [selectedWorkspaces, loadLinkedFiles]);

  // Set initial content in contentEditable div when textbox is shown
  useEffect(() => {
    // Show textbox if hasTextbox is true (there's existing content)
    if (hasTextbox && !showTextbox) {
      setShowTextbox(true);
    }
    
    if ((showTextbox || hasTextbox) && textboxEditorRef.current && !textboxInitializedRef.current) {
      // Mark as initialized first to prevent multiple runs
      textboxInitializedRef.current = true;
      
      // Use a small delay to ensure textboxContent state has been updated
      setTimeout(() => {
        if (textboxEditorRef.current) {
          // Set content from state (even if empty, to ensure div is initialized)
          const contentToSet = textboxContent || '';
          textboxEditorRef.current.innerHTML = contentToSet;
          
          // Move cursor to end after DOM update if there's content
          if (contentToSet) {
            requestAnimationFrame(() => {
              if (textboxEditorRef.current) {
                const range = document.createRange();
                const selection = window.getSelection();
                range.selectNodeContents(textboxEditorRef.current);
                range.collapse(false);
                selection?.removeAllRanges();
                selection?.addRange(range);
              }
            });
          }
        }
      }, 10);
    } else if (!showTextbox && !hasTextbox) {
      // Reset initialization flag when textbox closes and there's no content
      textboxInitializedRef.current = false;
    }
  }, [showTextbox, textboxContent, hasTextbox]); // Check both, but only initialize once

  // Combine linkedFiles and uploaded documents (excluding textbox)
  const allFiles = useMemo(() => {
    const uploadedFiles = documents.filter((doc) => doc.type === "uploaded");
    const linkedFileNames = new Set(linkedFiles.map((f) => f.name));
    // Only show uploaded files that aren't already in linkedFiles
    const uniqueUploaded = uploadedFiles.filter((doc) => !linkedFileNames.has(doc.name));
    return [...linkedFiles, ...uniqueUploaded];
  }, [linkedFiles, documents]);

  const handleAddTextbox = () => {
    const existingTextbox = documents.find((doc) => doc.type === "textbox");
    let contentToLoad = "";
    
    if (existingTextbox) {
      contentToLoad = existingTextbox.content || "";
    } else if (responseData?.textResponse?.html) {
      contentToLoad = responseData.textResponse.html;
    } else {
      // Try loading from localStorage
      const savedContent = localStorage.getItem(`textbox_${questionId}`);
      if (savedContent) {
        contentToLoad = savedContent;
      }
    }
    
    setTextboxContent(contentToLoad);
    setShowTextbox(true);
    // The useEffect will handle setting the innerHTML when showTextbox becomes true
  };

  const handleLinkDocument = (selectedDocuments: any[]) => {
    if (selectedDocuments.length === 0) return;
    
    setShowLinkDialog(false);
    
    const timestamp = Date.now();
    const newDocuments = selectedDocuments.map((doc, index) => ({
      id: `linked_${timestamp}_${index}`,
      type: "linked" as const,
      name: doc.name || "Unknown Document",
      reference: doc.reference || "",
      url: doc.url || "",
    }));
    
    const updated = [...documents, ...newDocuments];
    startTransition(() => {
      setDocuments(updated);
    });
    
    // Update db.json for all workspaces
    setTimeout(async () => {
      setIsLinking(true);
      try {
        const sectionCodes = selectedWorkspaces
          .map((ws) => getSectionCodeForQuestion(ws))
          .filter(Boolean);

        if ((window as any).sharePointAPI?.updateFileSections) {
          const fileNames = selectedDocuments.map((doc) => doc.name);
          
          // Update all section codes sequentially
          for (const sectionCode of sectionCodes) {
            await (window as any).sharePointAPI.updateFileSections({
              fileNames,
              sectionToAdd: sectionCode,
              folderName: "client",
            });
          }
          
          // Reload linked files
          setTimeout(() => {
            loadLinkedFiles();
          }, 500);
        }
        
        // Update parent state
        onUpdate({
          ...responseData,
          documents: updated,
        });
      } catch (error) {
        console.error("Error linking documents:", error);
      } finally {
        setIsLinking(false);
      }
    }, 100);
  };

  const handleUploadFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "*/*";
    
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;
      
      setIsUploading(true);
      try {
        const sectionCodes = selectedWorkspaces
          .map((ws) => getSectionCodeForQuestion(ws))
          .filter(Boolean);

        const uploadedFileNames: string[] = [];
        
        for (const file of files) {
          const fileContent = await readFileAsBase64(file);
          
          if ((window as any).sharePointAPI?.uploadFile) {
            const result = await (window as any).sharePointAPI.uploadFile({
              fileContent,
              fileName: file.name,
              folderName: "client",
              sectionList: sectionCodes.length > 0 ? [sectionCodes[0]] : [],
            });
            
            if (result?.success) {
              uploadedFileNames.push(file.name);
              const newDoc = {
                id: `upload_${Date.now()}_${Math.random()}`,
                type: "uploaded",
                name: file.name,
                url: result.data?.webUrl || "",
              };
              setDocuments((prev) => [...prev, newDoc]);
            }
          }
        }
        
        // Update db.json for all workspaces
        if (uploadedFileNames.length > 0 && (window as any).sharePointAPI?.updateFileSections) {
          for (const sectionCode of sectionCodes) {
            await (window as any).sharePointAPI.updateFileSections({
              fileNames: uploadedFileNames,
              sectionToAdd: sectionCode,
              folderName: "client",
            });
          }
          
          // Reload linked files
          setTimeout(() => {
            loadLinkedFiles();
          }, 500);
        }
        
        // Update parent state
        const updatedDocs = [...documents];
        for (const fileName of uploadedFileNames) {
          updatedDocs.push({
            id: `upload_${Date.now()}_${Math.random()}`,
            type: "uploaded",
            name: fileName,
            url: "",
          });
        }
        onUpdate({
          ...responseData,
          documents: updatedDocs,
        });
      } catch (error) {
        console.error("Error uploading file:", error);
        alert("Error uploading file");
      } finally {
        setIsUploading(false);
      }
    };
    
    input.click();
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const handleSaveTextbox = () => {
    if (textboxContent.trim()) {
      localStorage.setItem(`textbox_${questionId}`, textboxContent);
      setHasTextbox(true);
      
      const updatedDocuments = [...documents];
      const textboxIndex = updatedDocuments.findIndex((doc) => doc.type === "textbox");
      
      if (textboxIndex >= 0) {
        updatedDocuments[textboxIndex] = {
          ...updatedDocuments[textboxIndex],
          content: textboxContent,
        };
      } else {
        updatedDocuments.push({
          id: `textbox_${questionId}`,
          name: "Text Document",
          type: "textbox",
          content: textboxContent,
        });
      }
      
      setDocuments(updatedDocuments);
      onUpdate({
        ...responseData,
        textResponse: {
          html: textboxContent || "",
          plainText: (textboxContent || "").replace(/<[^>]*>/g, ""),
        },
        documents: updatedDocuments,
      });
    }
  };

  const handleCancelTextbox = () => {
    // Don't close textbox if there's existing content (hasTextbox is true)
    if (!hasTextbox) {
      setShowTextbox(false);
      setTextboxContent("");
    } else {
      // Just reload the original content if canceling edits
      const existingTextbox = documents.find((doc) => doc.type === "textbox");
      if (existingTextbox) {
        setTextboxContent(existingTextbox.content || "");
      } else if (responseData?.textResponse?.html) {
        setTextboxContent(responseData.textResponse.html);
      }
      // Reset the editor content
      if (textboxEditorRef.current) {
        const contentToRestore = existingTextbox?.content || responseData?.textResponse?.html || "";
        textboxEditorRef.current.innerHTML = contentToRestore;
      }
    }
  };

  const handleClearTextbox = () => {
    localStorage.removeItem(`textbox_${questionId}`);
    setTextboxContent("");
    setHasTextbox(false);
    setShowTextbox(false);
    const updatedDocuments = documents.filter((doc) => doc.type !== "textbox");
    setDocuments(updatedDocuments);
    onUpdate({
      ...responseData,
      textResponse: {
        html: "",
        plainText: "",
      },
      documents: updatedDocuments,
    });
  };

  const handleRemoveDocument = (id: string) => {
    const doc = documents.find((d) => d.id === id);
    const updated = documents.filter((doc) => doc.id !== id);
    setDocuments(updated);
    onUpdate({
      ...responseData,
      documents: updated,
    });
    
    if (doc && (doc.type === "uploaded" || doc.type === "linked")) {
      onFileOperation(questionId, doc.name, "remove");
    }
  };

  const handleUnlinkFile = async (fileName: string) => {
    try {
      const sectionCodes = selectedWorkspaces
        .map((ws) => getSectionCodeForQuestion(ws))
        .filter(Boolean);

      startTransition(() => {
        setLinkedFiles((prev) => prev.filter((file) => file.name !== fileName));
        setDocuments((prev) => {
          const updated = prev.filter((doc) => doc.name !== fileName);
          onUpdate({
            ...responseData,
            documents: updated,
          });
          return updated;
        });
      });

      // Remove sections from db.json
      if ((window as any).sharePointAPI?.removeFileSection) {
        setTimeout(() => {
          for (const sectionCode of sectionCodes) {
            (window as any).sharePointAPI.removeFileSection({
              fileName,
              sectionToRemove: sectionCode,
            }).then(() => {
              setTimeout(() => {
                loadLinkedFiles();
              }, 500);
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Failed to unlink file:", error);
      setTimeout(() => {
        loadLinkedFiles();
      }, 500);
    }
  };

  return (
    <Card className="border-white/10 bg-white/5">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-white text-lg">{title}</CardTitle>
          {onReviewCommentClick && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-amber-400 hover:text-amber-300 hover:bg-white/10"
              onClick={onReviewCommentClick}
              title="Review comments"
            >
              <MessageSquare className="h-5 w-5" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Display all files (linked + uploaded) above textbox */}
        {allFiles.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-white/80">Documents:</div>
            {allFiles.map((file) => (
              <div key={file.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/5 p-2">
                <FileText className="h-4 w-4 text-white/70 flex-shrink-0" />
                {file.url ? (
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={async (e) => {
                      e.preventDefault();
                      if ((window as any).electronWindow?.openExternal) {
                        try {
                          await (window as any).electronWindow.openExternal(file.url!);
                        } catch (error) {
                          console.error("Error opening external URL:", error);
                          window.open(file.url, "_blank", "noopener,noreferrer");
                        }
                      } else {
                        window.open(file.url, "_blank", "noopener,noreferrer");
                      }
                    }}
                    className="text-sm text-blue-300 hover:underline truncate flex-1 cursor-pointer"
                  >
                    {file.name}
                  </a>
                ) : (
                  <div className="text-sm text-white truncate flex-1">{file.name}</div>
                )}
                {!isArchivalMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (file.type === "uploaded") {
                        handleRemoveDocument(file.id);
                      } else {
                        handleUnlinkFile(file.name);
                      }
                    }}
                    className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0 rounded-full"
                    disabled={effectiveDisabled}
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add Document Dropdown */}
        <div className="flex items-center gap-2">
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-white/20 bg-black/40 text-white hover:bg-white/10"
                disabled={effectiveDisabled}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="border-white/20 bg-black/90 text-white">
              <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => handleAddTextbox(), 100); }} className="hover:bg-white/10 cursor-pointer">
                <FileText className="h-4 w-4 mr-2" />
                {hasTextbox ? "Edit Textbox" : "Textbox"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => setShowLinkDialog(true), 100); }} className="hover:bg-white/10 cursor-pointer">
                <Link className="h-4 w-4 mr-2" />
                Link existing workpaper
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => handleUploadFile(), 100); }} className="hover:bg-white/10 cursor-pointer" disabled={isUploading || effectiveDisabled}>
                <Upload className="h-4 w-4 mr-2" />
                {isUploading ? "Uploading..." : "Upload file"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Inline Textbox Editor */}
        {(showTextbox || hasTextbox) && (
          <div className="mt-3 p-4 border border-white/10 rounded bg-black/20">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">
                  {hasTextbox ? "Edit Text Document" : "Text Document"}
                </h4>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelTextbox}
                    className="border-white/20 bg-transparent text-white hover:bg-white/10"
                    disabled={effectiveDisabled}
                  >
                    Cancel
                  </Button>
                  {hasTextbox && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearTextbox}
                      className="border-red-500/20 bg-transparent text-red-400 hover:bg-red-500/10"
                      disabled={effectiveDisabled}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 p-2 border border-white/10 rounded bg-black/40">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand("bold")}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Bold className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand("italic")}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Italic className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand("underline")}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Underline className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-white/20 mx-1" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand("insertUnorderedList")}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand("insertOrderedList")}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <ListOrdered className="h-4 w-4" />
                </Button>
              </div>

              {/* Editor */}
              <div
                ref={textboxEditorRef}
                contentEditable={!effectiveDisabled}
                onInput={(e) => {
                  const html = e.currentTarget.innerHTML;
                  setTextboxContent(html);
                  localStorage.setItem(`textbox_${questionId}`, html);
                  onUpdate({
                    ...responseData,
                    textResponse: {
                      html: html || "",
                      plainText: (html || "").replace(/<[^>]*>/g, ""),
                    },
                    documents,
                  });
                }}
                className="min-h-[200px] p-3 border border-white/10 rounded bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 prose prose-invert max-w-none"
                style={{
                  fontFamily: "inherit",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
                data-placeholder="Enter your text here..."
              />
            </div>
          </div>
        )}

        {/* Link Document Dialog */}
        <LinkDocumentDialog
          isOpen={showLinkDialog}
          onClose={() => {
            setShowLinkDialog(false);
            setIsLinking(false);
          }}
          onLink={handleLinkDocument}
        />

        {/* Background linking indicator */}
        {isLinking && (
          <div className="fixed bottom-4 right-4 bg-black/80 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2 z-50 border border-white/20">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Updating SharePoint...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Helper component for questions with dropdown and documents
function QuestionWithDropdownAndDocuments({
  title,
  dropdownQuestionId,
  documentsQuestionId,
  dropdownValue,
  responseData,
  onDropdownChange,
  onDocumentsUpdate,
  selectedWorkspaces,
  workspaces,
  informationName,
  sharePointFiles,
  isLoadingSharePointFiles,
  onFileOperation,
  onReviewCommentClick,
}: {
  title: string;
  dropdownQuestionId: string;
  documentsQuestionId: string;
  dropdownValue?: string;
  responseData?: any;
  onDropdownChange: (value: string) => void;
  onDocumentsUpdate: (data: any) => void;
  selectedWorkspaces: string[];
  workspaces: Workspace[];
  informationName: string;
  sharePointFiles: Array<{ name: string }>;
  isLoadingSharePointFiles: boolean;
  onFileOperation: (questionId: string, fileName: string, operation: "add" | "remove") => void;
  onReviewCommentClick?: () => void;
}) {
  return (
    <Card className="border-white/10 bg-white/5">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-white text-lg">{title}</CardTitle>
          {onReviewCommentClick && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-amber-400 hover:text-amber-300 hover:bg-white/10"
              onClick={onReviewCommentClick}
              title="Review comments"
            >
              <MessageSquare className="h-5 w-5" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select value={dropdownValue || ""} onValueChange={onDropdownChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select approach" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Combination of substantive procedures and control testing">
              Combination of substantive procedures and control testing
            </SelectItem>
            <SelectItem value="Control Testing">Control Testing</SelectItem>
            <SelectItem value="Substantive Procedures">Substantive Procedures</SelectItem>
          </SelectContent>
        </Select>
        <QuestionWithDocuments
          title=""
          questionId={documentsQuestionId}
          responseData={responseData}
          onUpdate={onDocumentsUpdate}
          selectedWorkspaces={selectedWorkspaces}
          workspaces={workspaces}
          informationName={informationName}
          sharePointFiles={sharePointFiles}
          isLoadingSharePointFiles={isLoadingSharePointFiles}
          onFileOperation={onFileOperation}
        />
      </CardContent>
    </Card>
  );
}
